import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Bell, Users, Save, X } from "lucide-react";

const STAGE_LABELS = {
  unassigned: "Unassigned",
  analyst_working: "Analyst Working",
  attorney_review: "Attorney Review",
  vendor_review: "Vendor Review",
  gc_review: "GC Review",
  signature: "Signature",
  executed: "Executed",
};

export default function NotificationsAdmin() {
  const [cfg, setCfg] = useState({ stages: [], intervals: [] });
  const [users, setUsers] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  const [fromEmail, setFromEmail] = useState("");
  const [toUserId, setToUserId] = useState("");
  const [contracts, setContracts] = useState([]);

  const load = async () => {
    try {
      const [c, u] = await Promise.all([
        api.get("/admin/notifications/config"),
        api.get("/users").catch(() => ({ data: [] })),
      ]);
      setCfg(c.data);
      setUsers(u.data || []);
    } catch (e) { toast.error("Load failed"); }
  };
  useEffect(() => { load(); }, []);

  const saveStage = async (s) => {
    try {
      await api.post("/admin/notifications/config/stage", s);
      toast.success("Stage config saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const saveInterval = async (i) => {
    try {
      await api.post("/admin/notifications/config/interval", i);
      toast.success("Interval saved");
    } catch (e) { toast.error("Failed"); }
  };

  const findContracts = async () => {
    if (!fromEmail.trim()) return toast.error("Enter the email of the person leaving");
    try {
      // Look up contracts owned by this email
      const r = await api.get(`/contracts?owner_email=${encodeURIComponent(fromEmail)}`).catch(() => api.get("/contracts"));
      const all = r.data || [];
      const mine = all.filter(c =>
        ((c.submitted_by_email || "").toLowerCase() === fromEmail.toLowerCase()) ||
        ((c.on_behalf_of_email || "").toLowerCase() === fromEmail.toLowerCase())
      );
      setContracts(mine);
      toast.success(`Found ${mine.length} contracts`);
    } catch (e) { toast.error("Search failed"); }
  };

  const reassign = async () => {
    if (!toUserId) return toast.error("Choose new owner");
    if (selectedIds.length === 0) return toast.error("Select contracts");
    try {
      const r = await api.post("/admin/reassign-owner", {
        contract_ids: selectedIds,
        from_email: fromEmail,
        to_user_id: toUserId,
      });
      toast.success(`Reassigned ${r.data.updated} contract(s)`);
      setSelectedIds([]); setContracts([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="notifications-admin">
      <div className="flex items-center gap-3 mb-6">
        <Bell className="w-6 h-6 text-[#005289]" />
        <div>
          <h1 className="text-xl font-bold">Notifications & Owner Reassignment</h1>
          <p className="text-sm text-slate-500">Configure per-stage and pre-expiration notifications. Reassign owners in bulk when a person leaves.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-md" data-testid="stage-config">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 font-semibold">Per-stage notifications</div>
          <div className="p-4 space-y-3">
            {cfg.stages.map((s, i) => (
              <div key={s.stage} className="border border-slate-200 rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm">{STAGE_LABELS[s.stage] || s.stage}</div>
                  <label className="text-xs flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={s.enabled} onChange={e => {
                      const c = { ...cfg }; c.stages[i].enabled = e.target.checked; setCfg(c);
                    }} data-testid={`stage-enabled-${s.stage}`} />
                    Enabled
                  </label>
                </div>
                <input
                  className="w-full h-8 px-2 text-xs border border-slate-200 rounded"
                  placeholder="Recipients (comma-separated emails)"
                  value={(s.recipients || []).join(", ")}
                  onChange={e => {
                    const c = { ...cfg };
                    c.stages[i].recipients = e.target.value.split(",").map(x => x.trim()).filter(Boolean);
                    setCfg(c);
                  }}
                  data-testid={`stage-recipients-${s.stage}`}
                />
                <button className="mt-2 text-xs px-2 py-1 bg-[#005289] text-white rounded" onClick={() => saveStage(s)} data-testid={`stage-save-${s.stage}`}>
                  <Save className="w-3 h-3 inline mr-1" /> Save
                </button>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md" data-testid="interval-config">
          <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 font-semibold">Pre-expiration intervals</div>
          <div className="p-4 space-y-3">
            {cfg.intervals.map((iv, i) => (
              <div key={iv.days} className="border border-slate-200 rounded-md p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-semibold text-sm">{iv.days} days before expiration</div>
                  <label className="text-xs flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={iv.enabled} onChange={e => {
                      const c = { ...cfg }; c.intervals[i].enabled = e.target.checked; setCfg(c);
                    }} data-testid={`interval-enabled-${iv.days}`} />
                    Enabled
                  </label>
                </div>
                <input
                  className="w-full h-8 px-2 text-xs border border-slate-200 rounded"
                  placeholder="Recipients"
                  value={(iv.recipients || []).join(", ")}
                  onChange={e => {
                    const c = { ...cfg };
                    c.intervals[i].recipients = e.target.value.split(",").map(x => x.trim()).filter(Boolean);
                    setCfg(c);
                  }}
                  data-testid={`interval-recipients-${iv.days}`}
                />
                <button className="mt-2 text-xs px-2 py-1 bg-[#005289] text-white rounded" onClick={() => saveInterval(iv)} data-testid={`interval-save-${iv.days}`}>
                  <Save className="w-3 h-3 inline mr-1" /> Save
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-md mt-6" data-testid="reassign-panel">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50 font-semibold flex items-center gap-2">
          <Users className="w-4 h-4" /> Bulk owner reassignment (person leaves)
        </div>
        <div className="p-5">
          <div className="grid grid-cols-3 gap-3 mb-4">
            <input className="h-9 px-3 text-sm border border-slate-200 rounded" placeholder="Email of person leaving" value={fromEmail} onChange={e => setFromEmail(e.target.value)} data-testid="reassign-from" />
            <select className="h-9 px-3 text-sm border border-slate-200 rounded" value={toUserId} onChange={e => setToUserId(e.target.value)} data-testid="reassign-to">
              <option value="">— Choose new owner —</option>
              {users.map(u => <option key={u.id} value={u.id}>{u.name} ({u.email})</option>)}
            </select>
            <button className="h-9 px-4 bg-slate-700 text-white text-sm rounded" onClick={findContracts} data-testid="reassign-find">Find contracts</button>
          </div>

          {contracts.length > 0 && (
            <>
              <div className="text-xs text-slate-500 mb-2">Select the contracts to reassign to the new owner.</div>
              <div className="max-h-72 overflow-y-auto border border-slate-200 rounded">
                {contracts.map(c => (
                  <label key={c.id} className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 cursor-pointer hover:bg-slate-50">
                    <input type="checkbox"
                      checked={selectedIds.includes(c.id)}
                      onChange={e => setSelectedIds(e.target.checked ? [...selectedIds, c.id] : selectedIds.filter(x => x !== c.id))}
                      data-testid={`reassign-check-${c.contract_number}`}
                    />
                    <div className="flex-1 text-sm">
                      <div className="font-semibold">#{c.contract_number} · {c.vendor_legal_name}</div>
                      <div className="text-xs text-slate-500">{c.department} · {c.stage}</div>
                    </div>
                  </label>
                ))}
              </div>
              <button className="mt-3 h-9 px-4 bg-[#005289] text-white text-sm rounded" onClick={reassign} data-testid="reassign-execute">
                Reassign {selectedIds.length} contract(s)
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
